// hooks/useOptionFiles.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import apiClient from '../services/axiosInterceptor';

// The backend now builds and returns a full CDN `url` for every file
// (see helpers.WithFileURLs / R2_CDN_URL server-side) — no client-side
// URL construction needed here anymore.

export interface FileAttachment {
  path: string;
  url: string;
  file_name?: string;
  mime_type?: string;
  size?: number;
  type?: string;
  [key: string]: any;
}

interface OptionFilesEntry {
  option_id?: string;
  files: FileAttachment[];
  autoload?: string;
}

interface OptionFilesApiResponse {
  status: 'success' | 'error';
  data: Record<string, OptionFilesEntry>;
  message?: string;
}

export const useOptionFiles = (names: string[]) => {
  const [optionFiles, setOptionFiles] = useState<Record<string, OptionFilesEntry>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const namesKey = names.join(',');
  const abortRef = useRef<AbortController | null>(null);

  const fetchOptionFiles = useCallback(async () => {
    if (!namesKey) {
      setOptionFiles({});
      setLoading(false);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get<OptionFilesApiResponse>('api/options-files/multiple', {
        params: { names: namesKey },
        signal: controller.signal,
      });

      if (response.data.status === 'success') {
        setOptionFiles(response.data.data ?? {});
      } else {
        setError(response.data.message || 'Failed to load option files.');
      }
    } catch (err: any) {
      if (err?.name === 'CanceledError' || err?.name === 'AbortError') return;
      const message =
        err?.response?.data?.message || err?.message || 'An error occurred while fetching option files.';
      setError(message);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [namesKey]);

  useEffect(() => {
    fetchOptionFiles();
    return () => abortRef.current?.abort();
  }, [fetchOptionFiles]);

  return {
    optionFiles,
    loading,
    error,
    refetch: fetchOptionFiles,
  };
};